import React from 'react';
import { strings } from '../../resources/strings';
import { Order } from '../../models/Order';
import { StateLog } from '../../models/StateLog';
import { ErpOrder } from '../../models/ErpOrder';
import { StatePrescriptionLog } from '../../models/StatePrescriptionLog';
import { OrderNote } from '../../models/OrderNote';
import { HistoryByOrder } from '../../services/Orders/GetHistoryByOrder/HistoryByOrder';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { TabMenu } from '../../common-components/TabMenu/TabMenu';
import { Header } from './Header/Header';
import { TabItems } from './TabItems/TabItems';
import { TabCustomer } from './TabCustomer/TabCustomer';
import { TabHistoric } from './TabHistoric/TabHistoric';
import { TabFiscal } from './TabFiscal/TabFiscal';
import { TabOtherInformations } from './TabOtherInformations/TabOtherInformations';
import { Container, Content, Tab } from './styles';

const TabOptions = {
  Items: 'tab-items',
  Customer: 'tab-customer',
  Fiscal: 'tab-fiscal',
  Historic: 'tab-historic',
  OtherInformations: 'tab-other-infos'
}

export type FulfillmentItem = {
  id: number;
  fulfilled: boolean;
}

interface IOrderDetailsProps {
  orderId?: number;
  modalOpened?: () => void;
  modalClosed?: () => void;
}

const OrderDetails: React.FC<IOrderDetailsProps> = ({
  orderId,
  modalOpened,
  modalClosed
}) => {

  const { session } = useAuth();

  const [activeTab, setActiveTab] = React.useState<string>(TabOptions.Items);
  const [order, setOrder] = React.useState<Order | null | undefined>(null);
  const [lastStateLog, setLastStateLog] = React.useState<StateLog | null | undefined>(null);
  const [lastStatePrescriptionLog, setLastStatePrescriptionLog] = React.useState<StatePrescriptionLog | null | undefined>(null);
  const [historic, setHistoric] = React.useState<HistoryByOrder[]>([]);
  const [erpOrder, setErpOrder] = React.useState<ErpOrder | null | undefined>(null);
  const [notes, setNotes] = React.useState<OrderNote[]>([]);

  const {
    getOrderDetailsService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getDetails();
  }, [orderId]);

  const tabMenu = React.useMemo(() => {
    return (
      <TabMenu
        tabs={[
          {
            key: TabOptions.Items,
            text: strings.Items
          },
          {
            key: TabOptions.Customer,
            text: strings.Client
          },
          {
            key: TabOptions.Fiscal,
            text: strings.Fiscal
          },
          {
            key: TabOptions.OtherInformations,
            text: strings.OtherInformations
          },
          {
            key: TabOptions.Historic,
            text: strings.Historic
          }
        ]}
        activeKey={activeTab}
        change={(key) => setActiveTab(key)}
      />
    );
  }, [activeTab]);

  const tab = React.useMemo(() => {
    if (activeTab === TabOptions.Items) {
      return (
        <TabItems
          order={order}
          erpOrder={erpOrder}
          historic={historic}
        />
      );
    }

    if (activeTab === TabOptions.Customer) {
      return (
        <TabCustomer order={order} />
      );
    }

    if (activeTab === TabOptions.Historic) {
      return (
        <TabHistoric historic={historic} />
      );
    }

    if (activeTab === TabOptions.Fiscal) {
      return (
        <TabFiscal erpOrder={erpOrder} />
      );
    }

    if (activeTab === TabOptions.OtherInformations) {
      return (
        <TabOtherInformations order={order} orderNotes={notes} />
      );
    }

    return null;
  }, [activeTab, order, notes, erpOrder, historic]);

  return (
    <>
      <Container>
        <Header
          order={order}
          lastStateLog={lastStateLog}
          lastStatePrescriptionLog={lastStatePrescriptionLog}
          update={() => getDetails()}
          stateOrderChanged={() => getDetails()}
          statePrescriptionChanged={() => getDetails()}
          modalOpened={modalOpened}
          modalClosed={(changed) => handleModalClosed(changed)}
          userProfile={session?.user?.profileType}
        >
          {tabMenu}
        </Header>
        <Content>
          <Tab>
            {tab}
          </Tab>
        </Content>
      </Container>
    </>
  );

  function getDetails() {
    if (!orderId) return;
    getOrderDetailsService.handle({ orderId: orderId })
      .then(result => {
        setOrder(result?.value?.order);
        setLastStateLog(result?.value?.lastStateLog);
        setLastStatePrescriptionLog(result?.value?.lastStatePrescriptionLog);
        setHistoric(result?.value?.history || []);
        setErpOrder(result?.value?.firstErpOrder);
        setNotes(result?.value?.notes || []);
      });
  }

  function handleModalClosed(changed?: boolean) {
    if (modalClosed) modalClosed();

    if (!changed) return;

    getDetails();
  }

};

export { OrderDetails };